import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { InputField } from "components/designV2/inputfield/inputfield";
import { Modal } from "components/designV2/modal/modal";
import { TimePicker } from "components/designV2/timePicker/timePicker";
import { CompanyGigShift, GigType } from "model/Gig";
import { Setting } from "model/Workgroup";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Api } from "services/api/api.service";
import { useAlertStore } from "stores/alertStore/alertStore";
import { err, ok } from "utils/result";
import type { Result } from "utils/result";
import { ErrorModal } from "web-apps/company/components/common/errorModal.component";
import {
  buildShiftDates,
  normalizeTimeValue,
  parseTimeValue,
  toBreakMinutes,
  toStockholmDateFromDateOnly,
} from "web-apps/company/components/schedule/utils/schedule.dates";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { ScheduleCreatePayload } from "../scheduleRow/scheduleRow.component";
import {
  BreakRow,
  BreakSuffix,
  ModalContent,
  TimeRow,
  TimeSeparator,
} from "./quickScheduleModal.styled";

interface QuickScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  payload: ScheduleCreatePayload | null;
  onCreated: () => void;
}

type Defaults = {
  startTime: string;
  endTime: string;
  breakMinutes: string;
  deductionAmount: number;
};

type OverlappingShiftsResponse = {
  shifts: CompanyGigShift[];
};

const parseOverlappingShifts = (
  raw: CompanyGigShift[] | { shifts?: CompanyGigShift[] } | null | undefined,
): Result<CompanyGigShift[], "invalid-data"> => {
  if (Array.isArray(raw)) return ok(raw);
  if (raw && Array.isArray(raw.shifts)) return ok(raw.shifts);
  return err("invalid-data");
};

const getCompanySettingString = (
  settings: Setting[] | undefined,
  key: string,
  fallback: string,
): string => {
  const rawValue = settings?.find((setting) => setting.key === key)?.value;
  return rawValue ?? fallback;
};

const getCompanySettingNumber = (
  settings: Setting[] | undefined,
  key: string,
  fallback: number,
): number => {
  const rawValue = settings?.find((setting) => setting.key === key)?.value;
  const parsed = Number(rawValue ?? fallback);
  return Number.isNaN(parsed) ? fallback : parsed;
};

export const QuickScheduleModal: React.FC<QuickScheduleModalProps> = (
  props,
) => {
  const { t } = useTranslation();
  const [companyState] = useCompanyStore();
  const [, , alertDispatch] = useAlertStore();
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [breakMinutes, setBreakMinutes] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isOverlapErrorOpen, setIsOverlapErrorOpen] = useState(false);

  const companySettings: Setting[] =
    companyState.companySettings ?? companyState.company?.companySettings ?? [];

  const defaults = useMemo<Defaults>(() => {
    const rawStart = getCompanySettingString(
      companySettings,
      "CompanyGigShiftStartTime",
      "08:00",
    );
    const rawEnd = getCompanySettingString(
      companySettings,
      "CompanyGigShiftEndTime",
      "17:00",
    );
    const normalizedStart = normalizeTimeValue(rawStart, "08:00");
    const normalizedEnd = normalizeTimeValue(rawEnd, "17:00");
    const defaultBreakMinutes = String(
      Math.max(0, getCompanySettingNumber(companySettings, "GigBreakTime", 0)),
    );
    const deductionAmount = getCompanySettingNumber(
      companySettings,
      "GigPayDeduction",
      0,
    );

    return {
      startTime: normalizedStart,
      endTime: normalizedEnd,
      breakMinutes: defaultBreakMinutes,
      deductionAmount,
    };
  }, [companySettings]);

  useEffect(() => {
    if (!props.isOpen) return;
    setIsOverlapErrorOpen(false);
    setStartTime(defaults.startTime);
    setEndTime(defaults.endTime);
    setBreakMinutes(defaults.breakMinutes);
  }, [defaults, props.isOpen]);

  const startTimeParsed = useMemo(() => parseTimeValue(startTime), [startTime]);
  const endTimeParsed = useMemo(() => parseTimeValue(endTime), [endTime]);

  const canSave = useMemo(() => {
    if (!props.payload) return false;
    if (!companyState.company?.id) return false;
    if (!startTimeParsed || !endTimeParsed) return false;
    return !isSaving;
  }, [
    companyState.company?.id,
    endTimeParsed,
    isSaving,
    props.payload,
    startTimeParsed,
  ]);

  const handleClose = (): void => {
    if (isSaving) return;
    setIsOverlapErrorOpen(false);
    props.onClose();
  };

  const handleSave = async (): Promise<void> => {
    if (!props.payload || isSaving) return;
    const companyId = companyState.company?.id;
    if (!companyId) return;

    const dateResult = toStockholmDateFromDateOnly(props.payload.date);
    if (!dateResult.ok) return;

    const shiftDates = buildShiftDates(dateResult.value, startTime, endTime);
    if (!shiftDates.ok) return;

    const shift: CompanyGigShift = {
      startTime: shiftDates.value.startDate.toISOString(),
      endTime: shiftDates.value.endDate.toISOString(),
      breakTime: toBreakMinutes(breakMinutes),
      deductionAmount: defaults.deductionAmount,
    };

    const overlapPayload = {
      workerId: props.payload.workerId,
      workGroupId: props.payload.workGroupId,
      description: "",
      shifts: [shift],
      type: GigType.SOLO,
    };

    const createPayload = {
      ...overlapPayload,
    };

    setIsSaving(true);
    try {
      const overlapResponse = await Api().company.gig.getOverlappingGig(
        companyId,
        overlapPayload,
      );
      const parsedOverlap = parseOverlappingShifts(overlapResponse.data);
      const overlappingShifts = parsedOverlap.ok ? parsedOverlap.value : [];
      if (parsedOverlap.ok && overlappingShifts.length > 0) {
        setIsOverlapErrorOpen(true);
        return;
      }

      await Api().company.gig.createGig(companyId, createPayload);
      props.onCreated();
    } catch (err) {
      alertDispatch({
        type: "SHOW_ALERT",
        payload: {
          icon: "alert",
          title: t("Alerts.OhNo"),
          message: t("Alerts.SomethingWentWrong"),
        },
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      label={t("Schedule.QuickScheduleTitle")}
      isOpen={props.isOpen}
      onClose={handleClose}
      showCloseButton={false}
      size="compact"
      contentOverflow="visible"
      footer={
        <>
          <SecondaryButton label={t("Schedule.Cancel")} onClick={handleClose} />
          <PrimaryButton
            label={t("Schedule.Save")}
            onClick={handleSave}
            disabled={!canSave}
            isLoading={isSaving}
          />
        </>
      }
    >
      <ModalContent>
        <TimeRow>
          <TimePicker
            label={t("Schedule.StartTime")}
            value={startTime}
            onChange={setStartTime}
            size="compact"
            openOnFocus={false}
          />
          <TimeSeparator>-</TimeSeparator>
          <TimePicker
            label={t("Schedule.EndTime")}
            value={endTime}
            onChange={setEndTime}
            size="compact"
            openOnFocus={false}
          />
        </TimeRow>
        <BreakRow>
          <InputField
            label={t("Schedule.BreakMinutes")}
            value={breakMinutes}
            onChange={setBreakMinutes}
            type="number"
            size="compact"
          />
          <BreakSuffix>min</BreakSuffix>
        </BreakRow>
      </ModalContent>
      <ErrorModal
        isOpen={isOverlapErrorOpen}
        onClose={() => setIsOverlapErrorOpen(false)}
        titleKey="Schedule.EditShiftErrorTitle"
        messageKey="Schedule.QuickScheduleOverlapError"
      />
    </Modal>
  );
};
